import { useState, useEffect } from 'react'
import { UUID } from 'crypto'

import * as a9 from '../analytics'
import * as api from '../api'
import * as storage from '../storage'
import {
  handleRedirectOnEndOfService,
  handleRedirectOnMediaAccessDenied,
} from '../business'
import type { LicenceIds, MediaInfo, MediaPageForUser } from '../types/domain'
import { NavigationItem } from '../types/model'
import { isPageAnArticle } from '../types/utils'
import ReadingBar from './ReadingBar'
import getUserNotification from './getUserNotification'
import { DEFAULT_BAR_ACTIONS } from './constants'
import { BottomNavigation } from './components'
import { handleClickHome } from './ReadingBar/useReadingBar'

interface PageViewParams extends a9.BaseParams {
  url: string
  customerId: UUID
  licenceId: UUID
}

interface LoadingReadingBarProps {
  mediaUrl: string
}

const LoadingReadingBar = ({ mediaUrl }: LoadingReadingBarProps) => {
  const [pageForUser, setPageForUser] = useState<MediaPageForUser | undefined>()

  useEffect(() => {
    ;(async () => {
      const [licence, page] = await Promise.all([
        api.getUserLicence(),
        api.getPageDataByUrl(mediaUrl),
      ])

      if (licence === null || !licence?.isActive) {
        handleRedirectOnEndOfService()
        return
      }
      if (page === null) {
        return
      }
      const ids: LicenceIds = {
        customerId: licence.customer.id,
        licenceId: licence.id,
      }
      // 18/1/2026
      // Verificar si el medio esta habilitdo para el cliente y licencia activa
      const mediaInfo: MediaInfo[] | null = await api.getMediabyCustomerLicence(
        licence.customer.id,
        licence.id,
      )
      if (
        mediaInfo !== null &&
        mediaInfo.findIndex((m) => m.id === page.mediaProviderId) === -1
      ) {
        //alert("El medio de la página no está habilitado para el cliente - licencia. ");
        handleRedirectOnMediaAccessDenied()
        return
      }
      page.medias = mediaInfo?.map((m) => m.id)

      a9.updateBaseParams({
        userSub: storage.getUserSub(),
        articleId: page.articleId,
        mediaId: page.mediaProviderId,
      })

      const pageViewEv = isPageAnArticle(page.urlType)
        ? a9.Event.PAGE_VIEW_ARTICLE
        : a9.Event.PAGE_VIEW_MEDIA
      a9.dispatchEvent(pageViewEv, {
        url: mediaUrl,
        licenceId: licence.id,
        customerId: licence.customer.id,
      } as PageViewParams)

      const isFavorited = await api.getIsFavorited(
        page.mediaProviderId,
        page.articleId,
      )
      const notification = await getUserNotification(
        isPageAnArticle(page.urlType),
        page.isPremium,
        licence,
      )
      setPageForUser({ ...page, isFavorited: !!isFavorited, notification })
    })()
  }, [])

  if (pageForUser === undefined)
    return (
      <BottomNavigation
        actions={DEFAULT_BAR_ACTIONS}
        onClickNavItem={(navItem: NavigationItem) => {
          if (navItem === NavigationItem.Home) {
            handleClickHome()
          }
        }}
      />
    )

  const { isFavorited, notification, ...page } = pageForUser
  return (
    <ReadingBar
      page={page}
      isFavorited={isFavorited}
      notification={notification}
    />
  )
}

export default LoadingReadingBar
